// src/contexts/NotificationsContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  /**
   * Récupère le nombre de notifications non lues depuis Supabase
   */
  const fetchUnreadCount = useCallback(async () => {
    const storedUser = localStorage.getItem('crou_user');
    if (!storedUser) {
      setUnreadCount(0);
      return;
    }
    const user = JSON.parse(storedUser);

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_viewed', false);

    if (!error) {
      setUnreadCount(count || 0);
    }
  }, []);

  /**
   * Récupère toutes les notifications de l'utilisateur
   */
  const fetchNotifications = useCallback(async () => {
    const storedUser = localStorage.getItem('crou_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error) {
      setNotifications(data || []);
      return data || [];
    }
    return [];
  }, []);

  /**
   * Marquer une notification comme lue
   */
  const markAsRead = useCallback(async (notificationId) => {
    const { error } = await supabase
      .from('notifications')
      .update({ is_viewed: true, viewed_at: new Date().toISOString() })
      .eq('id', notificationId);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => n.id === notificationId ? { ...n, is_viewed: true } : n)
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  /**
   * Supprimer une notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    const notif = notifications.find((n) => n.id === notificationId);
    const wasUnread = notif && !notif.is_viewed;

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (!error) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, [notifications]);

  /**
   * Écouter les nouvelles notifications via Supabase Realtime
   */
  useEffect(() => {
    let channel = null;

    const setupRealtime = async () => {
      const storedUser = localStorage.getItem('crou_user');
      if (!storedUser) return;
      const user = JSON.parse(storedUser);

      // Charger le compteur initial
      fetchUnreadCount();

      // Souscrire aux nouvelles notifications en temps réel
      channel = supabase
        .channel('notifications_realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newNotif = payload.new;
            setNotifications((prev) => [newNotif, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        )
        .subscribe();
    };

    setupRealtime();

    // Vérifier périodiquement ou écouter un événement de connexion
    const interval = setInterval(() => {
      if (!channel) {
        setupRealtime();
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      if (channel) supabase.removeChannel(channel);
    };
  }, [fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        notifications,
        fetchUnreadCount,
        fetchNotifications,
        markAsRead,
        deleteNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé dans NotificationProvider');
  }
  return context;
};